import type { ServerError } from '../types';
import type { PreloadedAppState } from './init-app-state';
import { captureErrorMessage, captureTaggedException } from './analytics/error-reporting';
import type { Capability } from './analytics/capability-tags';

export type ServerSideRedirect = { redirectTo: string };

export const isRedirect = (
  data: PreloadedAppState | ServerSideRedirect | null
): data is ServerSideRedirect => Boolean(data && 'redirectTo' in data);

export const fetchPageState = async (
  restStateEndpoint: string,
  request: Request,
  tags: {
    capabilities: Capability[];
    throwOnClientError?: boolean;
  }
): Promise<PreloadedAppState | ServerSideRedirect | null> => {
  const url = new URL(request.url);

  const response = await fetchApiWithRetry(restStateEndpoint + url.search);

  if (response.status === 404) {
    captureTaggedException('Error fetching page state', {
      capabilities: tags.capabilities,
      extraProperties: {
        restStateEndpoint,
        responseStatus: response.status,
      },
    });
  }

  try {
    const data = await response.json();

    if (tags.throwOnClientError && response.status >= 400 && response.status < 500) {
      const message = `Failed to fetch ${restStateEndpoint} [${response.status} status]`;
      const errorType = (data?.error as ServerError) || { type: 'bad_request' };
      void captureErrorMessage(message, data);
      throw new ServerErrorException(message, response.status, errorType);
    }

    return data;
  } catch (parseError) {
    if (parseError instanceof ServerErrorException) {
      throw parseError;
    }
    captureErrorMessage(`Unexpected error while parsing AppState in Bifrost: ${parseError}`);
    return null;
  }
};

/**
 * Wrapper around fetch that throws an error for 5xx response status codes.
 *
 * @throws ServerErrorException if the response status code is 5xx.
 */
export const fetchApi = async (url: string, headers?: HeadersInit) => {
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });

  if (response.status >= 500) {
    const data = await response.json();
    const message = `Failed to fetch ${url} [${response.status} status]`;
    const errorType = (data?.error as ServerError) || { type: 'internal_server_error' };
    void captureErrorMessage(message, data);
    throw new ServerErrorException(message, response.status, errorType);
  }

  return response;
};

export class ServerErrorException extends Error {
  constructor(public message: string, public status: number, public errorType: ServerError) {
    super(message);
    this.name = 'ServerErrorException';
  }
}

export class FetchTimeoutError extends Error {
  constructor(public originalError: unknown) {
    const message =
      originalError instanceof Error
        ? `FetchTimeoutError: ${originalError.message}`
        : 'FetchTimeoutError';
    super(message);
    this.name = 'FetchTimeoutError';
  }
}

/**
 * Calls fetchApi with automatic retries when `fetchApi` throws an error (eg. network errors)
 * Server errors (e.g. `ServerErrorException`) are rethrown immediately.
 *
 * @throws `FetchTimeoutError` after all retry attempts are exhausted.
 */
export async function fetchApiWithRetry(
  url: string,
  headers?: HeadersInit,
  delayMs = 1000,
  retries = 2
): Promise<Response> {
  try {
    return await fetchApi(url, headers);
  } catch (error) {
    if (error instanceof ServerErrorException) {
      throw error;
    }

    if (retries <= 0) {
      captureErrorMessage(`Unexpected error while loading AppState in Bifrost: ${error}`);
      throw new FetchTimeoutError(error);
    }

    await new Promise(resolve => setTimeout(resolve, delayMs));
    return fetchApiWithRetry(url, headers, delayMs * 2, retries - 1);
  }
}
